import {
  Typography,
  Input,
  Switch,
  Stack,
  RadioGroup,
  RadioGroupItem,
  Label,
} from "@altimateai/lego";
import { AzureSetupGuide } from "./AzureSetupGuide";
import { OktaSetupGuide } from "./OktaSetupGuide";

type Provider = "azure" | "okta" | "other";

interface ProviderFallbackConfigProps {
  configForm: {
    provider: string;
    enable_graph_api_fallback: boolean;
    graph_api_tenant_id: string;
    enable_okta_api_fallback: boolean;
    okta_domain: string;
  };
  onUpdate: (
    field:
      | "provider"
      | "enable_graph_api_fallback"
      | "graph_api_tenant_id"
      | "enable_okta_api_fallback"
      | "okta_domain",
    value: string | boolean
  ) => void;
  disabled: boolean;
}

export const ProviderFallbackConfig = ({
  configForm,
  onUpdate,
  disabled,
}: ProviderFallbackConfigProps) => {
  const provider = configForm.provider as Provider;

  const handleProviderChange = (value: Provider) => {
    onUpdate("provider", value);
    if (value !== "azure") {
      onUpdate("enable_graph_api_fallback", false);
      onUpdate("graph_api_tenant_id", "");
    }
    if (value !== "okta") {
      onUpdate("enable_okta_api_fallback", false);
      onUpdate("okta_domain", "");
    }
  };

  return (
    <RadioGroup
      value={provider}
      onValueChange={v => handleProviderChange(v as Provider)}
      disabled={disabled}
      className="al-space-y-3"
    >
      {/* Azure AD */}
      <div className="al-space-y-3">
        <Stack direction="row" className="al-items-center al-gap-2">
          <RadioGroupItem value="azure" id="provider-azure" />
          <Label htmlFor="provider-azure" className="al-text-sm al-font-normal">
            Azure AD
          </Label>
        </Stack>
        {provider === "azure" && (
          <div className="al-pl-6 al-space-y-3">
            <AzureSetupGuide />
            <div className="al-rounded-md al-border al-p-4 al-space-y-3">
              <Stack direction="row" className="al-items-start al-gap-3">
                <Switch
                  checked={configForm.enable_graph_api_fallback}
                  onCheckedChange={checked => onUpdate("enable_graph_api_fallback", checked)}
                  disabled={disabled}
                  className="al-mt-0.5"
                />
                <div>
                  <Typography variant="body" className="al-text-sm al-font-medium">
                    Fetch groups via Microsoft Graph API
                  </Typography>
                  <Typography
                    variant="body"
                    className="al-text-xs al-text-muted-foreground al-mt-1"
                  >
                    Azure AD limits groups in tokens to ~200. When exceeded, groups are omitted
                    entirely. Enable this to retrieve them directly from the Graph API.
                  </Typography>
                </div>
              </Stack>
              {configForm.enable_graph_api_fallback && (
                <div className="al-pl-12 al-max-w-sm al-space-y-1">
                  <Typography variant="body" className="al-text-sm al-font-medium al-mb-1">
                    Azure Tenant ID
                  </Typography>
                  <Input
                    value={configForm.graph_api_tenant_id}
                    onChange={e => onUpdate("graph_api_tenant_id", e.target.value)}
                    placeholder="e.g. 12345678-abcd-..."
                  />
                  <Typography
                    variant="body"
                    className="al-text-xs al-text-muted-foreground al-mt-1"
                  >
                    Azure Portal &gt; Azure Active Directory &gt; Properties &gt; Tenant ID
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Okta */}
      <div className="al-space-y-3">
        <Stack direction="row" className="al-items-center al-gap-2">
          <RadioGroupItem value="okta" id="provider-okta" />
          <Label htmlFor="provider-okta" className="al-text-sm al-font-normal">
            Okta
          </Label>
        </Stack>
        {provider === "okta" && (
          <div className="al-pl-6 al-space-y-3">
            <OktaSetupGuide />
            <div className="al-rounded-md al-border al-p-4 al-space-y-3">
              <Stack direction="row" className="al-items-start al-gap-3">
                <Switch
                  checked={configForm.enable_okta_api_fallback}
                  onCheckedChange={checked => onUpdate("enable_okta_api_fallback", checked)}
                  disabled={disabled}
                  className="al-mt-0.5"
                />
                <div>
                  <Typography variant="body" className="al-text-sm al-font-medium">
                    Fetch groups via Okta Groups API
                  </Typography>
                  <Typography
                    variant="body"
                    className="al-text-xs al-text-muted-foreground al-mt-1"
                  >
                    When the groups claim is not in the SSO token, enable this to retrieve group
                    memberships directly from the Okta API.
                  </Typography>
                </div>
              </Stack>
              {configForm.enable_okta_api_fallback && (
                <div className="al-pl-12 al-max-w-sm al-space-y-1">
                  <Typography variant="body" className="al-text-sm al-font-medium al-mb-1">
                    Okta Domain
                  </Typography>
                  <Input
                    value={configForm.okta_domain}
                    onChange={e => onUpdate("okta_domain", e.target.value)}
                    placeholder="e.g. your-org.okta.com"
                  />
                  <Typography
                    variant="body"
                    className="al-text-xs al-text-muted-foreground al-mt-1"
                  >
                    Found in your Okta Admin Console URL
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Other */}
      <div className="al-space-y-2">
        <Stack direction="row" className="al-items-center al-gap-2">
          <RadioGroupItem value="other" id="provider-other" />
          <Label htmlFor="provider-other" className="al-text-sm al-font-normal">
            Other
          </Label>
        </Stack>
        {provider === "other" && (
          <Typography variant="body" className="al-pl-6 al-text-xs al-text-muted-foreground">
            Ensure your identity provider includes a groups claim in the SSO token. The claim name
            should match the Group Claim Name field above.
          </Typography>
        )}
      </div>
    </RadioGroup>
  );
};
